import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import useTimezoneDate from "../../../../hooks/useTimezoneDate";
import { Input } from "../../../Form";
import { ActionButtonProps } from "../../ActionButtons";
import { Body, EditModal } from "./styles";

export enum FieldType {
  TEXT = "text",
  DATE = "date",
}

export interface OnSaveEditFieldModalProps {
  id: number;
  column: string;
  value: any;
}

interface OpenModalProps {
  id: number;
  value: any;
  column: string;
  label: string;
  title: string;
  type: FieldType;
}

export interface EditFieldModalHandles {
  open: (props: OpenModalProps) => void;
  close: () => void;
}
interface EditFieldModalProps extends ActionButtonProps {
  onUpdate: (props: OnSaveEditFieldModalProps) => void;
}

const EditFieldModal: ForwardRefRenderFunction<EditFieldModalHandles, EditFieldModalProps> = (
  props,
  ref
) => {
  const { onUpdate, ...restProps } = props;

  const { t: translate } = useTranslation();
  const { getDateValueUtcTimezone } = useTimezoneDate();

  const [modalOpened, setModalOpened] = useState<boolean>(false);
  const [title, setTitle] = useState<string>(translate("edit"));
  const [fieldType, setFieldType] = useState<FieldType>();
  const [value, setValue] = useState<any>();
  const [column, setColumn] = useState<any>("");
  const [label, setLabel] = useState<any>("");
  const [id, setId] = useState<number>(0);

  const openModal = useCallback(({ id, value, column, label, title, type }: OpenModalProps) => {
    setModalOpened(true);

    if (title) setTitle(title);
    if (type) setFieldType(type);
    if (value) setValue(value);
    if (column) setColumn(column);
    if (label) setLabel(label);
    if (id) setId(id);
  }, []);

  const closeModal = useCallback(() => {
    setTitle(translate("edit"));
    setFieldType(undefined);
    setValue("");
    setColumn("");
    setId(0);
    setModalOpened(false);
  }, []);

  useImperativeHandle(ref, () => ({
    open: openModal,
    close: closeModal,
  }));

  const renderFieldType = () => {
    if (!fieldType) return null;
    switch (fieldType) {
      case FieldType.DATE:
        return (
          <Input
            label={label}
            type={fieldType}
            value={getDateValueUtcTimezone(value)}
            onChange={(event) => {
              setValue(event.target.value);
            }}
          />
        );
      case FieldType.TEXT:
      default:
        return (
          <Input
            label={label}
            type={fieldType}
            value={value}
            onChange={(event) => {
              setValue(event.target.value);
            }}
          />
        );
    }
  };

  const onSubmit = () => {
    let formatedValue = value;

    if (fieldType === FieldType.DATE) {
      formatedValue = new Date(value).toISOString();
    }
    onUpdate?.({ column, value: formatedValue, id });
  };

  return (
    <EditModal
      open={modalOpened}
      handleClose={closeModal}
      showModalFooter
      showActionButtons
      actionButtonProps={{
        textSave: translate("save"),
        showSaveButton: true,
        showCancelButton: true,
        onSave: () => onSubmit(),
        onCancel: closeModal,
        ...restProps,
      }}
      title={title}
      data-cy="edit-modal"
    >
      <Body data-cy="edit-modal-container">{renderFieldType()}</Body>
    </EditModal>
  );
};

export default forwardRef(EditFieldModal);
